import React, {useState, useMemo} from "react";
import {inject, observer} from "mobx-react";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
//local
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {showSuccess} from "helpers/notifications.helper";
import {
  IconTrumbsUp,
  IconTrumbsDown,
  IconFeather,
  IconChevronLeft,
} from "components/icons";
import {NotInterestedMessageModal} from "components/modals/NotInterestedMessageModal";
import {hasPermission} from "helpers/helper";
import {LockValue} from "components/LockValue";
import {color} from "constants/color.consts";
import OfferCard from "components/offerCard/OfferCard";

const ProposalCard = inject("store")(
  observer(({store: {buyer}, backToProposals}) => {
    const {currentRequest, currentOffer, offerIdWithContract} = buyer;
    const [isNotInterestedMessageModal, setIsNotInterestedMessageModal] = useState(false);
    const {t} = useTranslation();
    const location = useLocation();

    const evaluated = useMemo(() => currentOffer?.evaluate === true, [currentOffer]);
    const declined = useMemo(() => currentOffer?.evaluate === false, [currentOffer]);

    const create = useMemo(() => location.state?.offerId === "create", [location]);

    const changeEvaluate = (evaluate) => {
      // to prevent the same button from being pressed again
      const hasEvaluate = currentOffer?.evaluate !== null;
      const noNeedToPress =
        hasEvaluate && (evaluate === evaluated || !evaluate === declined);
      if (noNeedToPress) return;
      evaluate
        ? buyer
            .changeEvaluateStatus(currentOffer.id, {evaluate, rejectMessage: null})
            .then(() => showSuccess())
        : setIsNotInterestedMessageModal(true);
    };

    const onConfirmNotInterested = (rejectMessage) =>
      buyer
        .changeEvaluateStatus(currentOffer.id, {evaluate: false, rejectMessage})
        .then(() => showSuccess());

    const startContract = () => {
      if (!hasPermission(["CREATE_BUYER_CONTRACT"])) return;
      buyer.setInitialContract(true);
      buyer.setActiveRequestTab(3);
    };

    return (
      <div className="proposal-card">
        <div className="proposal-card_header __uppercase pb-6 mb-6">
          <ButtonTransparent
            noBorder
            name="OFFERS"
            bold
            small
            onClick={backToProposals}
            icon={<IconChevronLeft color={color.blue} addClass="mr-1" />}
            addClass="fz-16 black"
          />

          {!create && (
            <div className="df">
              {!offerIdWithContract && (
                <LockValue
                  canPermissions={["CREATE_BUYER_CONTRACT"]}
                  needLabel
                  needShowModalToSubscribe
                  noLockIcon
                >
                  <ButtonTransparent
                    addClass="create-agreement-button"
                    nonTransparent
                    small
                    name={t("ACCEPT_CREATE_AGREEMENT")}
                    type="button"
                    onClick={startContract}
                    icon={<IconFeather />}
                  />
                </LockValue>
              )}
              <LockValue
                canPermissions={["ENABLE_RFT_OFFERS_EVALUATE_OFFER"]}
                needLabel
                needShowModalToSubscribe
                noLockIcon
              >
                <ButtonTransparent
                  name={t("EVALUATE")}
                  addClass="evaluating-button ml-8"
                  small
                  type="button"
                  icon={<IconTrumbsUp color={evaluated && color.white} />}
                  onClick={() =>
                    hasPermission(["ENABLE_RFT_OFFERS_EVALUATE_OFFER"]) &&
                    changeEvaluate(true)
                  }
                  style={{
                    borderColor: color.green,
                    color: evaluated ? color.white : color.green,
                    backgroundColor: evaluated ? color.green : color.white,
                  }}
                />
              </LockValue>

              <LockValue
                canPermissions={["ENABLE_RFT_OFFERS_DECLINE_OFFER"]}
                needLabel
                needShowModalToSubscribe
                noLockIcon
              >
                <ButtonTransparent
                  name={t("DECLINE")}
                  addClass="no-thanks-button ml-2"
                  small
                  type="button"
                  icon={<IconTrumbsDown color={declined && color.white} />}
                  onClick={() =>
                    hasPermission(["ENABLE_RFT_OFFERS_EVALUATE_OFFER"]) &&
                    changeEvaluate(false)
                  }
                  style={{
                    borderColor: color.red,
                    color: declined ? color.white : color.red,
                    backgroundColor: declined ? color.red : color.white,
                  }}
                />
              </LockValue>
            </div>
          )}
        </div>
        <OfferCard
          offer={{
            ...currentOffer,
            smartFormQuestions: currentRequest?.smartFormQuestions,
          }}
          clientSide="buyer"
        />
        {isNotInterestedMessageModal && (
          <NotInterestedMessageModal
            title="NOT_INTERESTED_MESSAGE_BUYER_MODAL_TITLE"
            subtitle={t("NOT_INTERESTED_MESSAGE_BUYER_MODAL_SUBTITLE")}
            rejectMessage={currentOffer?.rejectMessage}
            onConfirm={onConfirmNotInterested}
            onClose={() => setIsNotInterestedMessageModal(false)}
          />
        )}
      </div>
    );
  })
);

export default ProposalCard;
